import React from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import es from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography, 
  Box, 
  Paper, 
  Chip,
  Avatar,
  IconButton,
  Grid,
  alpha 
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436'
};

const locales = {
  'es': es,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
})

const SupervisorCalendar = ({ deadlines = [] }) => {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedEvent, setSelectedEvent] = React.useState(null);

  const events = deadlines.map(deadline => ({
    title: `Fecha límite: ${deadline.course_title}`,
    start: new Date(deadline.deadline),
    end: new Date(deadline.deadline),
    allDay: true,
    resource: 'deadline',
    ...deadline
  }));

  const eventStyleGetter = (event) => {
    // Calcular días restantes
    const today = new Date();
    const deadlineDate = new Date(event.deadline);
    const daysRemaining = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
    
    let bgColor = COLORS.error;
    if (daysRemaining > 7) bgColor = COLORS.warning;
    if (daysRemaining > 14) bgColor = COLORS.success;

    return {
      style: {
        background: `linear-gradient(135deg, ${bgColor} 0%, ${alpha(bgColor, 0.8)} 100%)`,
        color: 'white',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        padding: '2px 8px',
        fontWeight: 600,
        fontSize: '0.8rem',
        boxShadow: `0 2px 8px ${alpha(bgColor, 0.3)}`
      }
    };
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEvent(null);
  };

  // Calcular días restantes para el dialog
  const getDaysRemaining = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    return Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '2px solid #e0e0e0',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <CalendarTodayIcon sx={{ color: 'white' }} />
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
              Calendario de Supervisión
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Fechas límite de formación del equipo
            </Typography>
          </Box>
        </Box>

        {/* Calendario */}
        <Box 
          sx={{ 
            height: 500,
            p: 2,
            '& .rbc-calendar': {
              fontFamily: 'inherit'
            },
            '& .rbc-header': {
              padding: '10px',
              fontWeight: 700,
              color: COLORS.dark,
              backgroundColor: alpha(COLORS.purple, 0.08),
              borderBottom: `2px solid ${alpha(COLORS.purple, 0.2)}`
            },
            '& .rbc-today': {
              backgroundColor: alpha(COLORS.success, 0.1)
            },
            '& .rbc-off-range-bg': {
              backgroundColor: '#f8f9fa'
            },
            '& .rbc-toolbar': {
              marginBottom: '16px',
              '& button': {
                color: COLORS.dark,
                fontWeight: 600,
                borderRadius: '8px',
                border: `1px solid ${alpha(COLORS.purple, 0.3)}`,
                '&:hover': {
                  backgroundColor: alpha(COLORS.purple, 0.1),
                  borderColor: COLORS.purple
                },
                '&.rbc-active': {
                  background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`,
                  color: 'white',
                  borderColor: 'transparent'
                }
              }
            },
            '& .rbc-toolbar-label': {
              fontWeight: 700,
              fontSize: '1.1rem',
              color: COLORS.dark
            },
            '& .rbc-month-view': {
              border: `1px solid ${alpha(COLORS.purple, 0.2)}`,
              borderRadius: '8px',
              overflow: 'hidden'
            },
            '& .rbc-day-bg + .rbc-day-bg': {
              borderLeft: `1px solid ${alpha(COLORS.purple, 0.1)}`
            },
            '& .rbc-month-row + .rbc-month-row': {
              borderTop: `1px solid ${alpha(COLORS.purple, 0.1)}`
            },
            '& .rbc-date-cell': {
              padding: '4px 8px',
              fontWeight: 500,
              '&.rbc-now': {
                fontWeight: 700,
                color: COLORS.purple
              }
            }
          }}
        >
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            culture='es'
            messages={{
              next: "Siguiente",
              previous: "Anterior",
              today: "Hoy",
              month: "Mes",
              week: "Semana",
              day: "Día"
            }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleEventClick}
          />
        </Box>
      </Paper>

      {/* Dialog de detalles */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.primary} 100%)`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EventIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Detalles de la Fecha Límite
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedEvent && (
            <Box>
              {/* Días restantes */}
              {(() => {
                const days = getDaysRemaining(selectedEvent.deadline);
                const color = days <= 0 ? COLORS.error : days <= 7 ? COLORS.warning : COLORS.success;
                return (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: alpha(color, 0.1),
                      border: `1px solid ${alpha(color, 0.3)}`,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="h3" sx={{ fontWeight: 800, color }}>
                      {days <= 0 ? '¡Vencido!' : days}
                    </Typography>
                    {days > 0 && (
                      <Typography sx={{ color, fontWeight: 600 }}>
                        {days === 1 ? 'día restante' : 'días restantes'}
                      </Typography>
                    )}
                  </Paper>
                );
              })()}

              {/* Info del empleado */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '2px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: alpha(COLORS.primary, 0.15),
                        color: COLORS.primary,
                        fontWeight: 700
                      }}
                    >
                      {selectedEvent.nombre?.[0]}{selectedEvent.apellido?.[0]}
                    </Avatar>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 18, color: COLORS.primary }} />
                        <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>
                          {selectedEvent.nombre} {selectedEvent.apellido}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
                        <Typography variant="body2" sx={{ color: '#636e72' }}>
                          {selectedEvent.department}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '2px solid #e0e0e0'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <SchoolIcon sx={{ color: COLORS.success }} />
                      <Typography variant="caption" sx={{ color: '#636e72', fontWeight: 600 }}>
                        CURSO ASIGNADO
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: COLORS.dark, fontSize: '1.1rem' }}>
                      {selectedEvent.course_title}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '2px solid #e0e0e0'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EventIcon sx={{ color: COLORS.error }} />
                      <Typography variant="caption" sx={{ color: '#636e72', fontWeight: 600 }}>
                        FECHA LÍMITE
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: COLORS.dark, fontSize: '1.1rem' }}>
                      {new Date(selectedEvent.deadline).toLocaleDateString('es-ES', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SupervisorCalendar;